import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { DataItemImportance, SubjectNote, Permission } from "../../misc/api/apiTypes";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { Site, WebPortalClient, ConfigurableDataItem } from "webportalclient";
import { useReferenceContextProvider } from "../../Core/ReferenceContextProvider";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { ConfigurableDataItemIds } from "../../misc/api/ApiConfigurableDataItems";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";
import { FileDownload } from "../../misc/FileDownload";

export const ViewSiteScreen = () => {
    const client = useWebPortalApiClient();
    const [dirty, setDirty] = React.useState(true); // indicates ui requires data initialization again
    const { dataItemConfigurationCollection, countries } = useCurrentStudyConfigurationContext();
    const { timeZones } = useReferenceContextProvider();
    const { id } = useParams<{ id: string }>();
    const [site, setSite] = React.useState<Site>();
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    
    React.useEffect(() => {
        const getData = async () => {
            setSite(null);
            const siteResult = await client.client.getSite(id, undefined);
            setSite(siteResult);
        }

        if (dirty)
            getData();

        setDirty(false);
    }, [dirty]);

    const replaceNullWithEmptyString = (value: string) => {
        return value ? value : "";
    }

    const convertCsvSpecialCharactersToASCII = (value: string | undefined | null) => {
        if (value === null || value === undefined) return value;

        return value.replace(',', '0x03')
                    .replace('\r', '0x04')
                    .replace('\n', '0x05');
    }
    const exportToCsv = () => {
        if (site) {
            if(site.number.includes(',') || site.number.includes('\n') || site.number.includes('\r')){
                setErrorAlert("Can't export site due to special characters in Site ID");
                return;
            }

            const headerRow = "Site Id,Site Name,Site Address,Site Email,Site Phone Number,Site Fax Number,Study Guid,Site Guid";

            const dataRow = `${site.number},${convertCsvSpecialCharactersToASCII(site.name)},${replaceNullWithEmptyString(convertCsvSpecialCharactersToASCII(site.address))},${replaceNullWithEmptyString(site.email)},${replaceNullWithEmptyString(site.phone)},${replaceNullWithEmptyString(site.fax)},${site.studyId},${site.id}`;

            const blobData = new Blob([headerRow + "\n" + dataRow], { type: "text/csv" });

            FileDownload(blobData, `Site_${site.number}.csv`, "text/csv");
        }
    }

    const isDisplayed = (item: ConfigurableDataItemIds) => {
        return dataItemConfigurationCollection.find(x => x.configurableDataItemId === item)?.importanceId !== DataItemImportance.NotApplicable;
    }

    const getColumnName = (itemId: ConfigurableDataItemIds | string) => {
        if (dataItemConfigurationCollection === null)
            return "";

        return dataItemConfigurationCollection.find(x => x.configurableDataItemId === itemId)?.displayText ?? itemId;
    };

    const readOnlyField = (id: string, itemId: ConfigurableDataItemIds | string, value: string | number) => (
        <div className="form-group col-md-4 col-sm-4 col-xs-12">
            <label htmlFor={id}>{getColumnName(itemId)}</label>
            <input id={id} className="form-control" disabled readOnly value={value} data-automationid={`Field_${id}`} />
        </div>
    )

    if (!site || !countries) return (<LoadingWithMessage message="Loading Site..." />);

    return (
        <>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">View Site</h2>
                </div>
                <div className="col-md-6 h2">
                    <div className="pull-right">
                        <button className="btn btn-primary" onClick={() => exportToCsv()}>
                            Export
                        </button>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div id="demographicsPanel" className="panel panel-primary">
                        <div className="panel-heading">
                            <h4>Details</h4>
                        </div>
                        <div className="panel-body">
                            {readOnlyField("site", ConfigurableDataItemIds.SiteNumber, site.number)}

                            {isDisplayed(ConfigurableDataItemIds.SiteName) ?
                                readOnlyField("name", ConfigurableDataItemIds.SiteName, site.name)
                                : null}

                            {isDisplayed(ConfigurableDataItemIds.SiteInvestigator) ?
                                readOnlyField("investigator", ConfigurableDataItemIds.SiteInvestigator, site.contact)
                                : null}

                            {isDisplayed(ConfigurableDataItemIds.SiteAddress) ?
                                readOnlyField("address", ConfigurableDataItemIds.SiteAddress, site.address)
                                : null}

                            {isDisplayed(ConfigurableDataItemIds.SiteCountry) ?
                                readOnlyField("country", ConfigurableDataItemIds.SiteCountry, site.country ? countries.find(c => c.countryCode === site.country)?.name : null)
                                : null}

                            {readOnlyField("timezone", "Time Zone", site.timeZone ? timeZones.find(tz => tz.id === site.timeZone).name : null)}

                            {isDisplayed(ConfigurableDataItemIds.SiteEmail) ?
                                readOnlyField("email", ConfigurableDataItemIds.SiteEmail, site.email)
                                : null}

                            {isDisplayed(ConfigurableDataItemIds.SitePhone) ?
                                readOnlyField("phone", ConfigurableDataItemIds.SitePhone, site.phone)
                                : null}
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="pull-right">
                        <Link className="btn btn-primary btn-outline" to="/Sites">
                            Return to List
                        </Link>
                    </div>
                </div>
            </div>
        </>
    );
}
